import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { terrainData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import { motion } from 'framer-motion';
import { 
  Users, GraduationCap, Zap, Handshake, MapPin, Calendar,
  Target, CheckCircle, ArrowRight, ChevronDown
} from 'lucide-react';

const TerrainPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = terrainData[language] || terrainData.fr;
  const [expandedMonth, setExpandedMonth] = useState(content.timeline[0]?.month);

  const iconMap = {
    Users: Users,
    GraduationCap: GraduationCap,
    Zap: Zap,
    Handshake: Handshake
  };

  const colorMap = {
    violet: { bg: isDark ? 'bg-[#5B2D8E]/20' : 'bg-[#5B2D8E]/10', text: isDark ? 'text-[#9B7FCE]' : 'text-[#5B2D8E]', border: 'border-[#5B2D8E]' },
    blue: { bg: isDark ? 'bg-blue-500/20' : 'bg-blue-100', text: 'text-blue-500', border: 'border-blue-500' },
    yellow: { bg: isDark ? 'bg-[#FFC107]/20' : 'bg-[#FFC107]/10', text: isDark ? 'text-[#FFC107]' : 'text-[#B8860B]', border: 'border-[#FFC107]' },
    green: { bg: isDark ? 'bg-green-500/20' : 'bg-green-100', text: 'text-green-500', border: 'border-green-500' }
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <Target className="w-16 h-16 mx-auto mb-6 text-white/80" />
              <h1 className="heading-1 text-white mb-4">{content.title}</h1>
              <p className="text-xl text-white/80 mb-4">{content.subtitle}</p>
              <p className="text-white/60">{content.description}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Summary */}
      <section className={`py-8 border-b ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-stone-200'}`}>
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: '4', label: 'Événements', icon: Calendar },
              { value: '3', label: 'Villes', icon: MapPin },
              { value: '70+', label: 'Participants', icon: Users },
              { value: '3', label: 'Partenariats', icon: Handshake }
            ].map((stat, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="text-center">
                  <stat.icon className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
                  <div className="text-3xl font-bold text-theme-primary">{stat.value}</div>
                  <div className="text-sm text-theme-secondary">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="heading-2 text-center mb-12">Chronologie des actions</h2>
            </AnimatedSection>

            <div className="relative">
              {/* Timeline line */}
              <div className={`absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 ${isDark ? 'bg-white/10' : 'bg-stone-200'} transform md:-translate-x-1/2`} />

              {content.timeline.map((monthData, monthIndex) => (
                <div key={monthData.month} className="mb-8">
                  {/* Month Header */}
                  <AnimatedSection delay={monthIndex * 0.1}>
                    <button
                      onClick={() => setExpandedMonth(expandedMonth === monthData.month ? null : monthData.month)}
                      className={`relative z-10 ml-8 md:ml-0 md:mx-auto flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-colors ${
                        expandedMonth === monthData.month
                          ? 'bg-[#5B2D8E] text-white'
                          : isDark ? 'bg-[#1C1917] text-stone-300 border border-white/10' : 'bg-white text-stone-600 border border-stone-200'
                      }`}
                    >
                      <Calendar className="w-4 h-4" />
                      {monthData.month}
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedMonth === monthData.month ? 'rotate-180' : ''}`} />
                    </button>
                  </AnimatedSection>

                  {/* Events */}
                  {expandedMonth === monthData.month && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-6 space-y-6"
                    >
                      {monthData.events.map((event, eventIndex) => {
                        const eventType = content.eventTypes[event.type];
                        const Icon = iconMap[eventType?.icon] || Zap;
                        const colors = colorMap[eventType?.color] || colorMap.violet;
                        
                        return (
                          <AnimatedSection key={event.id} delay={eventIndex * 0.1}>
                            <div className={`ml-8 md:ml-0 md:w-[calc(50%-2rem)] ${eventIndex % 2 === 0 ? 'md:mr-auto' : 'md:ml-auto'}`}>
                              <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                                {/* Event Header */}
                                <div className={`p-4 ${colors.bg} border-l-4 ${colors.border}`}>
                                  <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-white/10' : 'bg-white'} flex items-center justify-center`}>
                                      <Icon className={`w-5 h-5 ${colors.text}`} />
                                    </div>
                                    <div>
                                      <span className={`text-xs font-medium ${colors.text}`}>{eventType?.label}</span>
                                      <h3 className="font-semibold text-theme-primary">{event.title}</h3>
                                    </div>
                                  </div>
                                </div>

                                {/* Event Content */}
                                <div className="p-4 space-y-3">
                                  <div className="flex items-center gap-4 text-sm text-theme-secondary">
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" /> {event.location}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" /> {new Date(event.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                    </span>
                                  </div>

                                  <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-stone-50'}`}>
                                    <div className="flex items-start gap-2 mb-2">
                                      <Target className={`w-4 h-4 mt-0.5 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
                                      <div>
                                        <span className="text-xs font-medium text-theme-muted">Objectif</span>
                                        <p className="text-sm text-theme-secondary">{event.objective}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <CheckCircle className="w-4 h-4 mt-0.5 text-green-500" />
                                      <div>
                                        <span className="text-xs font-medium text-theme-muted">Résultat</span>
                                        <p className="text-sm text-theme-secondary">{event.result}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </AnimatedSection>
                        );
                      })}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              <MapPin className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              <h2 className="heading-3 mb-4">{content.cta.organize}</h2>
              <p className="text-theme-secondary mb-6">
                Vous souhaitez organiser une rencontre ADA dans votre ville ? Rejoignez-nous et créez votre cercle local.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/rejoindre" className="btn-primary">
                  {content.cta.join}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/evenements" className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-stone-300 text-stone-600 hover:bg-stone-100'} transition-colors`}>
                  Voir les événements
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TerrainPage;
